import { Object3D, PlaneGeometry, ShaderMaterial, Color, DoubleSide, Mesh } from "three";
import color from "./colors.js";
import { about, PHRASE } from "./index.js";
import { random, randomSign, smoothstep } from "~/lib/utils.js";
import { config } from "./config";
import { vertexShader, fragmentShader } from "~/assets/shaders/about/ring";

export default class Ring extends Object3D {
    constructor() {
        super();

        this._speed = random(1, 3) * 0.005;

        this.geometry = new PlaneGeometry(1, 1);
        this.material = new ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                color: { value: new Color(color.orange) },
                globalProgress: { value: 0 },
                progress: { value: 0 },
                direction: { value: randomSign() },
                strokeWidth: { value: config.rings.strokeWidth },
                randomSeed: { value: random() },
            },
            transparent: true,
            side: DoubleSide,
        });

        this.mesh = new Mesh(this.geometry, this.material);

        this.farthestDistance = 1000;

        this.add(this.mesh);
        this._init();
    }

    _init() {
        this.progress = 0;
        this.position.z =
            about.camera.position.z - random(5, this.farthestDistance);

        const size = random(0.5, 1) * config.rings.size;

        this.direction = randomSign();
        this.scale.setScalar(size);
    }

    update(globalProgress) {
        if (!this.material || !this.material.uniforms) return;

        const speed =
            this._speed *
            config.rings.speed *
            (1 + smoothstep(0.5, 1, globalProgress) * 10);

        this.material.uniforms.globalProgress.value = globalProgress;
        this.progress += speed;

        if (this.isEnd || this.isOutOfView) {
            if (about.phrase !== PHRASE.ENDING) {
                this._init();
            }
        }
    }

    destroy() {
        this.geometry?.dispose();
        this.material?.dispose();
    }

    set strokeWidth(value) {
        this.material.uniforms.strokeWidth.value = value;
    }

    set direction(value) {
        this.material.uniforms.direction.value = value;
    }

    set progress(value) {
        this.material.uniforms.progress.value = value;
    }

    get progress() {
        return this.material.uniforms.progress.value;
    }

    get isEnd() {
        return this.progress > 1;
    }

    get isOutOfView() {
        const cameraZ = about.camera.position.z;
        return (
            this.position.z > cameraZ ||
            this.position.z < cameraZ - this.farthestDistance
        );
    }
}
